// 🔧 promotion_content_studio.js
// ✅ Purpose: Promotion Studio for the LGV Promotion Engine
// ✅ Features:
// - Displays Promotion Ideas on the left
// - Provides a step-by-step human workflow
// - Supports AI draft generation review
// - Supports AI image generation review
// - Requires human acceptance before AI content enters the editor
// - Creates Promotion Posts in Draft status
// - Supports editing existing Promotion Posts
// - Saves edited Promotion Posts back to the Promotion Engine queue
// - Uses shared Promotion Engine components and hooks
// - Uses LGV notification banner with 20-second auto-hide
// - Fixes hook ordering, missing usePromotionPosts import, and edit-mode loading issues
// - Fully block-commented for clarity and future devs

import React, {
  useEffect,
  useState,
  useRef,
} from "react";

import {
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import SeoHead from "../../../components/seo_head";
import { useRole } from "../../../role_context";
import { usePromotionAiUsage } from "../../../promotion/hooks/use_promotion_ai_usage";
import { getSourcePromotionIdea } from "../../../promotion/services/promotion_source_service";
import LGVNotificationBanner from "../../../components/lgv/lgv_notification_banner";

import PromotionPageHeader from "../../../promotion/components/promotion_page_header";
import PromotionSectionCard from "../../../promotion/components/promotion_section_card";
import PromotionActionNavigation from "../../../promotion/components/promotion_action_navigation";
import PromotionImageUpload from "../../../promotion/components/promotion_image_upload";
import PromotionVideoUpload from "../../../promotion/components/promotion_video_upload";
import PromotionPostOptions from "../../../promotion/components/promotion_post_options";
import { getPromotionPostCapabilities, normalizePromotionPostOptions, normalizePromotionHashtags, buildPromotionPostText } from "../../../../functions/shared/promotion_post_capabilities.js";
import { getPromotionVideoConstraints } from "../../../promotion/constants/promotion_video";
import { validatePromotionVideoMetadata } from "../../../promotion/services/promotion_video_upload_service";
import PromotionPostSizesModal from "../../../promotion/components/promotion_post_sizes_modal";
import PromotionSizedImagesModal from "../../../promotion/components/promotion_sized_images_modal";
import PromotionScheduleForm from "../../../promotion/components/promotion_schedule_form";
import PromotionImageCropModal from "../../../promotion/components/promotion_image_crop_modal";
import { generateSizedPromotionImages } from "../../../promotion/services/promotion_sizing_service";
import { getAuctionPromotionIdea } from "../../../promotion/services/promotion_auction_service";
import { getPromotionPostsForIdea } from "../../../promotion/services/promotion_post_service";
import { getPromotionEditorImages } from "../../../promotion/services/promotion_source_images";
import { getPromotionPostTypeLabel } from "../../../promotion/constants/promotion_post_types";
import {
  createPromotionIdea,
  getPromotionIdea,
  savePromotionIdeaContext,
  updatePromotionIdeaStatus,
} from "../../../promotion/services/promotion_idea_service";
import { PROMOTION_IDEA_STATUS } from "../../../promotion/constants/promotion_status";
import { PROMOTION_WORKFLOW, getPromotionWorkflowState, getPromotionHistoryLabel, getPromotionSocialPostUrl, canSchedulePromotionPost } from "../../../promotion/helpers/promotion_workflow";

import {
  usePromotionContentStudio,
} from "../../../promotion/hooks/use_promotion_content_studio";

import {
  usePromotionAI,
} from "../../../promotion/hooks/use_promotion_ai";

import {
  usePromotionImage,
} from "../../../promotion/hooks/use_promotion_image";

import {
  usePromotionPosts,
} from "../../../promotion/hooks/use_promotion_posts";
import {
  usePromotionSettings,
} from "../../../promotion/hooks/use_promotion_settings";
import {
  PROMOTION_SCOPES,
} from "../../../promotion/config/promotion_settings_map";
import {
  useEventContext,
} from "../../../hooks/use_event_context";

import {
  PROMOTION_ENABLED_PLATFORM_OPTIONS,
} from "../../../promotion/constants/promotion_platforms";

import {
  getSupportedPostTypesForPlatform,
  getPromotionPostTypeOptionsForPlatform,
  getPromotionSizedPostType,
} from "../../../promotion/constants/promotion_post_types";

import {
  PROMOTION_CATEGORIES,
  PROMOTION_CATEGORY_GROUPS,
} from "../../../promotion/constants/promotion_categories";
import {
  PROMOTION_MESSAGE_FOCUS_OPTIONS,
} from "../../../promotion/constants/promotion_message_focuses";
import {
  EVENT_TYPES,
} from "../../../constants/event_types";

const PROMOTION_CONTENT_CATEGORIES = PROMOTION_CATEGORIES.filter(
  (item) => item.group !== PROMOTION_CATEGORY_GROUPS.INDUSTRIES
);

const LEGACY_CATEGORY_EVENT_TYPES = {
  car_shows: "car_show",
  art_shows: "art_show",
  pet_shows: "pet_show",
  schools: "school_election",
  festivals: "festival",
  nonprofits: "nonprofit",
};

/**
 * ============================================================================
 * PromotionContentStudio
 * ============================================================================
 */

const PromotionContentStudio = () => {
  const navigate = useNavigate();
  const { eventslug } = useParams();
  const { eventId, eventDoc, eventLoading } = useEventContext({ redirectIfNotFound: false });
  const { role } = useRole() || {};
  const isManager = Boolean(eventId && role !== "admin");
  const dataReady = !eventslug || Boolean(eventId);
  const promotionUrl = (page) => eventslug ? `/${eventslug}/${page}` : `/${page}`;
  const ideasUrl = promotionUrl("promotion_dashboard");

  const [searchParams, setSearchParams] = useSearchParams();

  const editingPostId = searchParams.get("post");
  const requestedIdeaId = searchParams.get("idea");
  const requestedPlatform = searchParams.get("platform");
  const requestedAuctionItem = searchParams.get("auctionItem");
  const requestedSource = searchParams.get("source");
  const requestedSourceId = searchParams.get("sourceId");
  const requestedSourceScope = searchParams.get("sourceScope");
  const requestedDraftKey = searchParams.get("draft");
  const editorIdentity = JSON.stringify([eventId, editingPostId, requestedIdeaId, requestedAuctionItem, requestedSource, requestedSourceId, requestedSourceScope]);
  const editorIdentityRef = useRef(editorIdentity);
  editorIdentityRef.current = editorIdentity;
  const retainedIdeaRef = useRef(null);
  const saveInFlightRef = useRef(false);

  /**
   * --------------------------------------------------------------------------
   * Promotion Engine Hooks
   * --------------------------------------------------------------------------
   */

  const {
    saveDraft,
    saving: savingPostDraft,
  } = usePromotionContentStudio();

  const {
    selectedPost,
    loadPost,
    savePostChanges,
    approvePost,
    schedulePost,
    deletePost,
    saving: postSaving,
    invalidatePostsCache,
  } = usePromotionPosts({ eventId, enabled: dataReady, loadList: false });
  const isDraftPost = editingPostId && getPromotionWorkflowState(selectedPost) === PROMOTION_WORKFLOW.DRAFT;

  const {
    settings,
  } = usePromotionSettings();

  const {
    generating,
    error,
    generateDraft,
  } = usePromotionAI();

  const {
    generatingImage,
    imageError,
    generateImage,
  } = usePromotionImage();

  /**
   * --------------------------------------------------------------------------
   * Page State
   * --------------------------------------------------------------------------
   */

  const [selectedIdea, setSelectedIdea] = useState(null);
  const [ideaSaving, setIdeaSaving] = useState(false);
  const saving = savingPostDraft || ideaSaving;
  const ideaHasPrompt = typeof selectedIdea?.aiPrompt === "string" && Boolean(selectedIdea.aiPrompt.trim());

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [cta, setCta] = useState("");
  const [postOptions, setPostOptions] = useState({});
  const [imagePrompt, setImagePrompt] = useState("");
  const [prompt, setPrompt] = useState("");
  const [platform, setPlatform] = useState("");
  const [postType, setPostType] = useState("");
  const [category, setCategory] = useState("");
  const [images, setImages] = useState([]);
  const [rawVideo, setRawVideo] = useState(null);
  const [sizedImages, setSizedImages] = useState([]);
  const [sizingBusy, setSizingBusy] = useState(false);
  const [sizingProgress, setSizingProgress] = useState("");
  const [cropQueue, setCropQueue] = useState([]);
  const sizingController = useRef(null);
  const [imagePreviewPost, setImagePreviewPost] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUploadBusy, setUploadBusy] = useState(false);
  const [videoUploadBusy, setVideoUploadBusy] = useState(false);
  const uploadBusy = imageUploadBusy || videoUploadBusy;
  const [showPostSizes, setShowPostSizes] = useState(false);
  const [ideaPosts, setIdeaPosts] = useState([]);
  const [ideaPostsLoading, setIdeaPostsLoading] = useState(false);
  const [ideaPostsError, setIdeaPostsError] = useState("");
  const [ideaPostsRevision, setIdeaPostsRevision] = useState(0);
  const ideaHistoryIdentityRef = useRef("");
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceError, setSourceError] = useState("");
  const [promptRequired, setPromptRequired] = useState(false);
  const [savedPrompt, setSavedPrompt] = useState("");
  const [draftKey, setDraftKey] = useState("");
  const requestType = "social_post";
  const [topic, setTopic] = useState("");
  const [eventType, setEventType] = useState("");
  const [notification, setNotification] = useState({
    show: false,
    type: "success",
    message: "",
  });
  const postTypeOptions = platform
    ? getPromotionPostTypeOptionsForPlatform(platform).map((type) => ({ value: type.key, label: type.label }))
    : [];
  const legacyFormat = getSupportedPostTypesForPlatform(platform).find((type) => type.key === postType);
  if (legacyFormat && !postTypeOptions.some((option) => option.value === postType)) {
    postTypeOptions.unshift({ value: postType, label: `${legacyFormat.label} (choose a size)` });
  }
  const selectedFormat = getPromotionSizedPostType(platform, postType) || legacyFormat;
  const postCapabilities = getPromotionPostCapabilities(platform, postType);
  const maxImages = selectedFormat?.maxImages || 0;
  const videoConstraints = getPromotionVideoConstraints(platform, postType);
  const cropImage = cropQueue[0];
  const existingCropCopy = cropImage && sizedImages.find((copy) => copy.sourceUrl === cropImage.url && copy.postType === postType);
  // Rebuild in original order. A changed source/format never reuses stale copies.
  const currentSizedImages = images.map((image) => sizedImages.find((copy) => copy.sourceUrl === image.url && copy.postType === postType && copy.width === selectedFormat?.width && copy.height === selectedFormat?.height)).filter(Boolean);
  const mediaForPost = currentSizedImages.length > 0 && currentSizedImages.length === images.length ? currentSizedImages : images;
  const { usage: aiUsage, loading: aiUsageLoading, error: aiUsageError, refreshUsage } = usePromotionAiUsage({
    eventId, postId: editingPostId, draftKey,
    enabled: isManager && dataReady && !sourceLoading && !sourceError,
  });
  const contentLimitReached = isManager && (!aiUsage || aiUsageLoading || Boolean(aiUsageError) || (aiUsage.limited && aiUsage.contentRemaining <= 0));
  const imageLimitReached = isManager && (!aiUsage || aiUsageLoading || Boolean(aiUsageError) || (aiUsage.limited && aiUsage.imageRemaining <= 0));

  useEffect(() => {
    if (!isManager || editingPostId || !dataReady) { setDraftKey(""); return; }
    const key = requestedDraftKey || crypto.randomUUID();
    setDraftKey(key);
    if (!requestedDraftKey) {
      const next = new URLSearchParams(searchParams);
      next.set("draft", key);
      setSearchParams?.(next, { replace: true });
    }
    // Identity changes drive this effect; unrelated query values do not create
    // another allowance for the same draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManager, editingPostId, dataReady, eventId, requestedIdeaId, requestedAuctionItem, requestedSource, requestedSourceId, requestedSourceScope, requestedDraftKey, setSearchParams]);

  // A new route owns a new editor session. Never save stale content to a
  // different post when its load fails, or carry generated copy into a new draft.
  useEffect(() => {
    // Saving the current Idea promotes only its route identity. The editor's
    // body/media/options remain in progress and must not be rehydrated/reset.
    if (retainedIdeaRef.current?.toIdentity === editorIdentity) return undefined;
    retainedIdeaRef.current = null;
    setSelectedIdea(null);
    setTitle(""); setBody(""); setHashtags(""); setCta("");
    setPostOptions({});
    setImagePrompt(""); setPrompt(""); setPlatform(""); setPostType("");
    setCategory(""); setImages([]); setRawVideo(null); setVideoUploadBusy(false); setTopic("");
    setPromptRequired(false);
    setSavedPrompt("");
    setSizedImages([]); setCropQueue([]); setImagePreviewPost(null); setImagePreview(null); setSizingBusy(false); setSizingProgress("");
    sizingController.current?.abort();
    sizingController.current = null;
    setSourceError("");
    setSourceLoading(Boolean(requestedAuctionItem || requestedSource || requestedIdeaId || editingPostId));
    return () => sizingController.current?.abort();
  }, [requestedAuctionItem, requestedSource, requestedSourceId, requestedSourceScope, requestedIdeaId, editingPostId, eventId, editorIdentity]);

  // A new format cannot inherit options intended for another social endpoint.
  useEffect(() => {
    const capabilities = getPromotionPostCapabilities(platform, postType);
    setPostOptions((previous) => normalizePromotionPostOptions(platform, postType, previous, { strict: false }));
    if (!capabilities.supportsHashtags) setHashtags("");
    if (!capabilities.supportsCaption) setCta("");
  }, [platform, postType]);

  // Browser history/format changes invalidate an open crop and any late output.
  useEffect(() => {
    setCropQueue([]);
    sizingController.current?.abort();
    sizingController.current = null;
    setSizingBusy(false); setSizingProgress("");
  }, [platform, postType]);

  useEffect(() => {
    if (!requestedSource || !eventId || editingPostId || requestedIdeaId) return undefined;
    let canceled = false;
    getSourcePromotionIdea({ eventId, source: requestedSource, sourceId: requestedSourceId, ...(requestedSourceScope ? { sourceScope: requestedSourceScope } : {}) })
      .then((idea) => {
        if (canceled) return;
        setSelectedIdea(idea); setSourceLoading(false);
        navigate(`${eventslug ? `/${eventslug}` : ""}/promotion_studio?idea=${encodeURIComponent(idea.id)}`, { replace: true });
      })
      .catch((failure) => {
        if (!canceled) { setSourceError(failure.message); setNotification({ show: true, type: "error", message: failure.message }); }
      })
      .finally(() => { if (!canceled) setSourceLoading(false); });
    return () => { canceled = true; };
  }, [requestedSource, requestedSourceId, requestedSourceScope, requestedIdeaId, editingPostId, eventId, eventslug, navigate]);

  useEffect(() => {
    if (!requestedAuctionItem || !eventId || editingPostId || requestedIdeaId) return undefined;
    let cancelled = false;
    setSourceLoading(true);
    getAuctionPromotionIdea({ eventId, auctionItemId: requestedAuctionItem })
      .then((idea) => {
        if (cancelled) return;
        setSelectedIdea(idea);
        setSourceLoading(false);
        // Replace the source query with its durable idea identity for refresh/reuse.
        navigate(`${promotionUrl("promotion_studio")}?idea=${encodeURIComponent(idea.id)}`, { replace: true });
      })
      .catch((loadError) => {
        if (!cancelled) {
          setSourceError(loadError.message);
          setNotification({ show: true, type: "error", message: loadError.message });
        }
      })
      .finally(() => { if (!cancelled) setSourceLoading(false); });
    return () => { cancelled = true; };
    // Route identity, rather than the render-local URL helper, controls loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedAuctionItem, requestedIdeaId, editingPostId, eventId, eventslug, navigate]);

  useEffect(() => {
    const identity = JSON.stringify([eventId, selectedIdea?.id]);
    if (ideaHistoryIdentityRef.current !== identity) {
      setIdeaPosts([]);
      ideaHistoryIdentityRef.current = identity;
    }
    // Keep same-Idea rows mounted on refresh so one saved schedule cannot
    // erase date/time inputs being composed for another platform record.
    setIdeaPostsError("");
    if (!selectedIdea?.id || !dataReady) return undefined;
    let cancelled = false;
    setIdeaPostsLoading(true);
    getPromotionPostsForIdea({ ideaId: selectedIdea.id, eventId: eventId || null })
      .then((posts) => { if (!cancelled) setIdeaPosts(posts); })
      .catch((loadError) => { if (!cancelled) setIdeaPostsError(loadError.message || "Unable to load promotions."); })
      .finally(() => { if (!cancelled) setIdeaPostsLoading(false); });
    return () => { cancelled = true; };
  }, [selectedIdea?.id, eventId, dataReady, ideaPostsRevision]);

  useEffect(() => {
    if (!platform) return;
    if (getPromotionSizedPostType(platform, postType)) return;
    const supportedTypes = getSupportedPostTypesForPlatform(platform);
    if (supportedTypes.some((type) => type.key === postType)) return;
    setPostType(getPromotionPostTypeOptionsForPlatform(platform)[0]?.key || "");
  }, [platform, postType]);

  /**
   * --------------------------------------------------------------------------
   * Auto-hide notification after 20 seconds
   * --------------------------------------------------------------------------
   */

  useEffect(() => {
    if (!notification.show) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setNotification((current) => ({
        ...current,
        show: false,
      }));
    }, 20000);

    return () => clearTimeout(timer);
  }, [notification]);

  useEffect(() => {
    if (!dataReady || !requestedIdeaId || editingPostId) return undefined;
    if (retainedIdeaRef.current?.toIdentity === editorIdentity) return undefined;
    let cancelled = false;

    getPromotionIdea(requestedIdeaId)
      .then(async (idea) => {
        if ((idea.eventId || null) !== (eventId || null)) throw new Error("This idea belongs to another event.");
        const editorImages = await getPromotionEditorImages({ record: idea, eventId });
        if (!cancelled) {
          setSelectedIdea({ ...idea, editorImages });
        }
      })
      .catch((loadError) => {
        if (!cancelled) {
          setSourceError(loadError.message);
          setNotification({ show: true, type: "error", message: loadError.message });
        }
      })
      .finally(() => { if (!cancelled) setSourceLoading(false); });

    return () => { cancelled = true; };
  }, [editingPostId, requestedIdeaId, eventId, dataReady, editorIdentity]);

  /**
   * --------------------------------------------------------------------------
   * Populate fields when an idea is selected
   * --------------------------------------------------------------------------
   */

  useEffect(() => {
    const retained = retainedIdeaRef.current;
    if (retained && retained.ideaId === selectedIdea?.id && [retained.fromIdentity, retained.toIdentity].includes(editorIdentity)) return;
    if (!selectedIdea || editingPostId || (requestedIdeaId && selectedIdea.id !== requestedIdeaId) || (!requestedIdeaId && !requestedAuctionItem && !requestedSource)) {
      return;
    }
    setTopic(selectedIdea.topic || "");
    setImages(selectedIdea.editorImages || (selectedIdea.imageUrls || []).map((url) => ({ url, storagePath: "" })));

    setTitle(selectedIdea.title || "");
    setPlatform(requestedPlatform || selectedIdea.platforms?.[0] || "");
    setPrompt(selectedIdea.aiPrompt || `${selectedIdea.title || ""}\n\n${selectedIdea.description || ""}`.trim());
    const legacyEventType = LEGACY_CATEGORY_EVENT_TYPES[selectedIdea.category] || "";
    setCategory(legacyEventType ? "" : selectedIdea.category || "");
    if (!eventId) {
      setEventType(selectedIdea.eventType || legacyEventType);
    }
  }, [
    selectedIdea,
    editingPostId,
    eventId,
    requestedPlatform,
    requestedIdeaId,
    requestedAuctionItem,
    requestedSourceId,
    requestedSource,
    requestedSourceScope,
    editorIdentity,
  ]);

  useEffect(() => {
    if (eventId) {
      setEventType(eventDoc?.eventType || "");
    }
  }, [eventDoc?.eventType, eventId]);

  /**
   * --------------------------------------------------------------------------
   * Load Existing Draft
   * --------------------------------------------------------------------------
   */

  useEffect(() => {
    if (!dataReady || !editingPostId) {
      return;
    }

    let cancelled = false;

    const loadExistingPost = async () => {
      try {
        const post = await loadPost(editingPostId);

        if (cancelled) {
          return;
        }
        if ((post.eventId || null) !== (eventId || null)) throw new Error("This post belongs to another event.");
        const editorImages = await getPromotionEditorImages({ record: post, eventId, isSavedPost: true });
        if (cancelled) return;

        setTitle(post.title || "");
        setBody(post.body || "");
        // Legacy drafts have no Idea snapshot; seed editable context from the
        // saved record only, never from another platform's post or live source.
        setPrompt(post.aiPrompt || [post.ideaTitle || post.title, post.body].filter(Boolean).join("\n\n"));
        setSavedPrompt(post.aiPrompt || "");
        setHashtags((post.hashtags || []).join(" "));
        setCta(post.cta || "");
        setPostOptions(post.postOptions || {});
        setImagePrompt(post.imagePrompt || "");
        setPlatform(post.platform || "");
        setPostType(post.postType || "");
        setTopic(post.topic || "");
        const legacyEventType = LEGACY_CATEGORY_EVENT_TYPES[post.category] || "";
        if (!eventId) {
          setEventType(post.eventType || legacyEventType);
        }
        setCategory(legacyEventType ? "" : post.category || "");
        setImages(editorImages);
        setSizedImages(Array.isArray(post.sizedImages) ? post.sizedImages : []);
        setRawVideo(post.rawVideo || (post.videoUrl ? { ...(post.videoMetadata || {}), url: post.videoUrl, storagePath: post.videoStoragePath || "" } : null));
        setSelectedIdea(post.ideaId ? { id: post.ideaId, title: post.ideaTitle || "", relatedModule: post.relatedModule || "", relatedId: post.relatedId || "", relatedName: post.relatedName || "", relatedScope: post.relatedScope || "", sourceAuctionItemId: post.sourceAuctionItemId || "" } : null);
      } catch (err) {
        if (cancelled) {
          return;
        }

        console.error(err);
        setSourceError(err.message || "Unable to load Promotion Post.");

        setNotification({
          show: true,
          type: "error",
          message:
            err.message ||
            "Unable to load Promotion Post.",
        });
      } finally {
        if (!cancelled) setSourceLoading(false);
      }
    };

    loadExistingPost();

    return () => {
      cancelled = true;
    };
  }, [
    editingPostId,
    eventId,
    dataReady,
    loadPost,
  ]);


  /**
   * --------------------------------------------------------------------------
   * handleGenerateAiDraft
   * --------------------------------------------------------------------------
   */

    const handleGenerateAiDraft = async () => {
      const identity = editorIdentity;
      const cleanPrompt = prompt.trim();
      const cleanTitle = title.trim();
      const cleanBody = body.trim();
      setPromptRequired(!cleanPrompt);

    if (!cleanPrompt) {
      setNotification({
        show: true,
        type: "warning",
        message: "AI Prompt / Idea is required.",
      });

      return;
    }
    if (contentLimitReached) return;

    if (!platform.trim()) {
      setNotification({
        show: true,
        type: "warning",
        message: "Platform is required.",
      });

      return;
    }

      try {
        const aiResult = await generateDraft({
          ideaTitle:
            selectedIdea?.title ||
            cleanTitle ||
            "Promotion Post",

          ideaDescription:
            selectedIdea?.description ||
            cleanPrompt ||
            cleanBody,

          requestType,
          topic,
          userInstruction: cleanPrompt,
          platform,
          postType,
          category,
          eventType,
          eventId: eventId || null,
          ...(editingPostId ? { postId: editingPostId } : { draftKey: draftKey || null }),
        });

        if (aiResult && editorIdentityRef.current === identity) {

          setTitle(aiResult.content?.title || "");
          setBody(aiResult.content?.body || "");
          setHashtags((aiResult.content?.hashtags || []).join(" "));
          setCta(aiResult.content?.cta || "");
          setImagePrompt(aiResult.content?.imagePrompt || "");

          setNotification({
            show: true,
            type: "success",
            message: "AI draft generated and added to the editor.",
          });
        }
      } catch (err) {
        if (editorIdentityRef.current !== identity) return;
        console.error(err);

        setNotification({
          show: true,
          type: "error",
          message:
            err.message ||
            "Unable to generate AI draft.",
        });
      } finally {
        if (editorIdentityRef.current === identity) await refreshUsage();
      }
    };

  /**
   * --------------------------------------------------------------------------
   * handleGenerateImage
   * --------------------------------------------------------------------------
   */

  const handleGenerateImage = async () => {
    const identity = editorIdentity;
    if (imageLimitReached || maxImages <= 0) return;
    try {
      const generatedImage = await generateImage({
        title: title.trim() || body.trim().slice(0, 80),
        body,
        imagePrompt,
        platform,
        postType,
        eventId: eventId || null,
        ...(editingPostId ? { postId: editingPostId } : { draftKey: draftKey || null }),
      });

      if (editorIdentityRef.current !== identity) return;
      setImages((previous) => [...previous, { url: generatedImage.imageUrl, storagePath: generatedImage.storagePath || "" }]);

      setNotification({
        show: true,
        type: "success",
        message: "AI image generated and added to the editor.",
      });
    } catch (err) {
      if (editorIdentityRef.current !== identity) return;
      console.error(err);

      setNotification({
        show: true,
        type: "error",
        message:
          err.message ||
          "Unable to generate AI image.",
      });
    } finally {
      if (editorIdentityRef.current === identity) await refreshUsage();
    }
  };

  /**
   * --------------------------------------------------------------------------
   * handleSaveDraft
   * --------------------------------------------------------------------------
   */

  const validatePost = () => {
    if (!prompt.trim()) {
      setPromptRequired(true);
      setNotification({ show: true, type: "error", message: "AI Prompt / Idea is required." });
      return false;
    }
    if (sourceLoading || sourceError || uploadBusy || sizingBusy || cropQueue.length || generating || generatingImage) return false;
    if (!body.trim()) {
      setNotification({ show: true, type: "error", message: "Post Body is required." });
      return false;
    }
    if (!platform) {
      setNotification({ show: true, type: "error", message: "Platform is required." });
      return false;
    }
    if (!postType) {
      setNotification({ show: true, type: "error", message: "Post Type is required." });
      return false;
    }
    if (!selectedFormat) {
      setNotification({ show: true, type: "error", message: "Choose a Post Type supported by the selected platform." });
      return false;
    }
    try {
      normalizePromotionPostOptions(platform, postType, postOptions, { imageCount: maxImages ? images.length : 0, videoDurationSeconds: rawVideo?.durationSeconds });
      if (postCapabilities.supportsCaption) buildPromotionPostText({ title: "", body, cta, hashtags: normalizePromotionHashtags(platform, postType, hashtags), platform, postType });
    } catch (error) {
      setNotification({ show: true, type: "error", message: error.message || "Check the post options." });
      return false;
    }
    if (maxImages > 0 && images.length > maxImages) {
      setNotification({ show: true, type: "error", message: `This Post Type allows ${maxImages} image(s). Remove extra images or choose another Post Type.` });
      return false;
    }
    if (videoConstraints && rawVideo) {
      try {
        validatePromotionVideoMetadata(rawVideo, videoConstraints);
        if (rawVideo.contentType !== "video/mp4" || !Number.isFinite(rawVideo.sizeBytes) || rawVideo.sizeBytes <= 0 || rawVideo.sizeBytes > videoConstraints.maxBytes) {
          throw new Error("Upload an MP4 within the video size limit before saving.");
        }
      } catch (error) {
        setNotification({ show: true, type: "error", message: error.message || "This video does not meet the upload limits." });
        return false;
      }
    }
    return true;
  };

  const getPostUpdates = () => ({
    // Publish body only; inherited AI/source titles are internal drafting context.
    title: "",
    body,
    hashtags: normalizePromotionHashtags(platform, postType, hashtags),
    cta: postCapabilities.supportsCaption ? cta.trim() : "",
    postOptions: normalizePromotionPostOptions(platform, postType, postOptions, { imageCount: maxImages ? images.length : 0, videoDurationSeconds: rawVideo?.durationSeconds }),
    imagePrompt,
    aiPrompt: prompt.trim(),
    platform,
    postType,
    requestType,
    topic,
    eventType,
    category,
    imageUrl: maxImages ? mediaForPost[0]?.url || "" : "",
    imageStoragePath: maxImages ? mediaForPost[0]?.storagePath || "" : "",
    imageUrls: maxImages ? mediaForPost.map((image) => image.url) : [],
    imageStoragePaths: maxImages ? mediaForPost.map((image) => image.storagePath || "") : [],
    rawImages: images.map(({ url, storagePath }) => ({ url, storagePath: storagePath || "" })),
    sizedImages: maxImages ? currentSizedImages : [],
    // Keep the original across format changes, but attach it only to video posts.
    rawVideo,
    videoUrl: videoConstraints ? rawVideo?.url || "" : "",
    videoStoragePath: videoConstraints ? rawVideo?.storagePath || "" : "",
    ...(!editingPostId && draftKey ? { aiUsageKey: draftKey } : {}),
    ...(!editingPostId && selectedIdea ? {
      relatedModule: selectedIdea.relatedModule || "",
      relatedId: selectedIdea.relatedId || "",
      relatedName: selectedIdea.relatedName || "",
      ...(selectedIdea.relatedScope ? { relatedScope: selectedIdea.relatedScope } : {}),
      sourceAuctionItemId: selectedIdea.sourceAuctionItemId || "",
    } : {}),
  });

  const handleApplyCrop = async (crop) => {
    if (!cropImage || sizingBusy || !selectedFormat?.width) return;
    const controller = new AbortController();
    sizingController.current = controller;
    setSizingBusy(true);
    try {
      await generateSizedPromotionImages({ eventId: eventId || null, platform, postType, images: [{ ...cropImage, crop }], signal: controller.signal,
        onProgress: setSizingProgress,
        onImage: (copy) => {
          if (!controller.signal.aborted) setSizedImages((previous) => [...previous.filter((image) => image.sourceUrl !== copy.sourceUrl || image.postType !== copy.postType), copy]);
        },
      });
      if (!controller.signal.aborted) {
        setCropQueue((previous) => previous.slice(1));
        setNotification({ show: true, type: "success", message: "Sized image copy generated. Save the promotion to keep the image and crop." });
      }
    } catch (error) {
      if (!controller.signal.aborted) setNotification({ show: true, type: "error", message: error.message || "Unable to generate sized images." });
      if (!controller.signal.aborted) throw error;
    } finally {
      if (sizingController.current === controller) {
        setSizingBusy(false); setSizingProgress(""); sizingController.current = null;
      }
    }
  };

  const ensureSavedIdea = async () => {
    const identity = editorIdentity;
    const cleanPrompt = prompt.trim();
    if (!cleanPrompt) throw new Error("AI Prompt / Idea is required.");
    const idea = selectedIdea
      ? ideaHasPrompt ? selectedIdea : { ...selectedIdea, ...await savePromotionIdeaContext({ ideaId: selectedIdea.id, aiPrompt: cleanPrompt, eventId: eventId || null }) }
      : await createPromotionIdea({
        title: title.trim() || cleanPrompt.split(/\r?\n/)[0].slice(0, 80), description: cleanPrompt, aiPrompt: cleanPrompt,
        category, platforms: platform ? [platform] : [], postTypes: postType ? [postType] : [],
        eventId: eventId || null, scope: eventId ? PROMOTION_SCOPES.EVENT : PROMOTION_SCOPES.INTERNAL_LGV,
      });
    if (editorIdentityRef.current !== identity) return null;
    const toIdentity = JSON.stringify([eventId, null, idea.id, null, null, null, null]);
    retainedIdeaRef.current = { fromIdentity: identity, toIdentity, ideaId: idea.id };
    setSelectedIdea(idea);
    if (requestedIdeaId !== idea.id) {
      const next = new URLSearchParams(searchParams);
      ["post", "source", "sourceId", "sourceScope", "auctionItem"].forEach((key) => next.delete(key));
      next.set("idea", idea.id);
      setSearchParams(next, { replace: true });
    }
    return idea;
  };

  const handleSaveIdea = async () => {
    if (saveInFlightRef.current || editingPostId || sourceLoading || sourceError || !dataReady || eventLoading || !settings.enabled) return;
    if (!prompt.trim()) {
      setPromptRequired(true);
      setNotification({ show: true, type: "error", message: "AI Prompt / Idea is required." });
      return;
    }
    saveInFlightRef.current = true; setIdeaSaving(true);
    try {
      const idea = await ensureSavedIdea();
      if (idea) setNotification({ show: true, type: "success", message: "Idea saved. Continue below to create a Promotion Post." });
    } catch (error) {
      if (editorIdentityRef.current === editorIdentity) setNotification({ show: true, type: "error", message: error.message || "Unable to save Idea." });
    } finally { saveInFlightRef.current = false; setIdeaSaving(false); }
  };

  const handleSaveDraft = async () => {
    if (saveInFlightRef.current) return;
    saveInFlightRef.current = true;
    const identity = editorIdentity;
    const isCurrentEditor = () => editorIdentityRef.current === identity ||
      (retainedIdeaRef.current?.fromIdentity === identity && retainedIdeaRef.current?.toIdentity === editorIdentityRef.current);
    try {
      if (!validatePost()) return;
      setIdeaSaving(true);

      if (editingPostId) {
        await savePostChanges(editingPostId, getPostUpdates());
        setSavedPrompt(prompt.trim());
      } else {
        const ideaForPost = await ensureSavedIdea();
        if (!ideaForPost) return;
        const createdPost = await saveDraft({
          ideaId: ideaForPost.id,
          ideaTitle: ideaForPost.title,
          ...getPostUpdates(),
          eventId: eventId || null,
          scope: eventId ? PROMOTION_SCOPES.EVENT : PROMOTION_SCOPES.INTERNAL_LGV,
        });

        try {
          await updatePromotionIdeaStatus({ ideaId: ideaForPost.id, ideaStatus: PROMOTION_IDEA_STATUS.CONVERTED_TO_QUEUE });
        } catch (error) {
          // The post already exists; lifecycle metadata must not turn a saved
          // post into a reported failure that encourages a duplicate retry.
          console.warn("[Promotion Studio] Post saved; Idea tracking update failed", { code: error.code || "unknown" });
        }

        if (!isCurrentEditor()) return;
        navigate(`${promotionUrl("promotion_studio")}?post=${createdPost.id}`, { replace: true });
      }

      if (!isCurrentEditor()) return;
      setNotification({
        show: true,
        type: "success",
        message: editingPostId
          ? "Promotion Post updated successfully."
          : "Promotion Post saved and added to Ideas for social tracking.",
      });

      invalidatePostsCache();
      setIdeaPostsRevision((revision) => revision + 1);
    } catch (err) {
      if (!isCurrentEditor()) return;
      console.error(err);

      setNotification({
        show: true,
        type: "error",
        message:
          err.message ||
          "Unable to save Promotion Post.",
      });
    } finally { saveInFlightRef.current = false; setIdeaSaving(false); }
  };

  const handleScheduleDraft = async () => {
    if (!editingPostId || !validatePost()) return;
    if (!window.confirm(`Approve this ${platform} Social Post for scheduling?`)) return;

    try {
      await savePostChanges(editingPostId, getPostUpdates());
      await approvePost(editingPostId);
      invalidatePostsCache();
      await loadPost(editingPostId);
      setIdeaPostsRevision((revision) => revision + 1);
      setNotification({ show: true, type: "success", message: "This promotion record is approved. Set its posting date and time below." });
      document.getElementById(`promotion-schedule-${editingPostId}`)?.scrollIntoView?.({ block: "center", behavior: "smooth" });
    } catch (err) {
      setNotification({ show: true, type: "error", message: err.message || "Unable to move the promotion draft to Scheduling." });
    }
  };

  const handleScheduleRecord = async (post, { postId, scheduledAt }) => {
    if (postId !== post.id || !canSchedulePromotionPost(post)) throw new Error("This record is not available for scheduling.");
    if (post.queueStatus !== "approved") await approvePost(postId);
    await schedulePost({ postId, scheduledAt });
    invalidatePostsCache();
    setIdeaPostsRevision((revision) => revision + 1);
    // Scheduling changes one saved record; it must not overwrite unsaved editor content.
    if (editingPostId === postId) {
      try { await loadPost(postId); }
      catch (error) { console.warn("[Promotion Studio] Scheduled record status could not be refreshed", { code: error.code || "unknown" }); }
    }
  };

  const handleDeleteDraft = async () => {
    if (!editingPostId) return;
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;

    try {
      await deletePost(editingPostId);
      invalidatePostsCache();
      navigate(ideasUrl, {
        state: { notification: { type: "success", message: "Promotion draft deleted." } },
      });
    } catch (err) {
      setNotification({ show: true, type: "error", message: err.message || "Unable to delete the promotion draft." });
    }
  };

  /**
   * --------------------------------------------------------------------------
   * Render
   * --------------------------------------------------------------------------
   */

  return (
    <>
      {showPostSizes && <PromotionPostSizesModal onClose={() => setShowPostSizes(false)} />}
      {imagePreviewPost && <PromotionSizedImagesModal post={imagePreviewPost} onClose={() => setImagePreviewPost(null)} />}
      {imagePreview && <PromotionSizedImagesModal images={imagePreview.images} initialIndex={imagePreview.index} title="Image Preview" onClose={() => setImagePreview(null)} />}
      {cropImage && selectedFormat?.width && <PromotionImageCropModal
        key={`${cropImage.url}:${postType}`} image={cropImage} width={selectedFormat.width} height={selectedFormat.height}
        {...(existingCropCopy ? { initialCrop: existingCropCopy.crop ?? null } : {})}
        busy={sizingBusy} onApply={handleApplyCrop} onClose={() => { if (!sizingBusy) setCropQueue([]); }} />}
      <SeoHead
        title="Promotion Studio | Let's Go Vote"
        description="Promotion Studio"
        noindex
      />

      <div className="container-fluid py-4">
        <div
          className="container"
          style={{
            maxWidth: "1500px",
          }}
        >
          <PromotionPageHeader
            title={
              editingPostId
                ? "Edit Promotion Post"
                : "Promotion Studio"
            }
            subtitle={
              editingPostId
                ? "Edit an existing Promotion Post."
                : "Turn ideas into Promotion Posts ready for approval, scheduling, and publishing."
            }
          />

          <PromotionActionNavigation current="drafting" promotionUrl={promotionUrl} />

          <LGVNotificationBanner
            show={notification.show}
            type={notification.type}
            message={notification.message}
            onClose={() =>
              setNotification((current) => ({
                ...current,
                show: false,
              }))
            }
          />

          {!settings.enabled && (
            <div className="alert alert-warning">
              Promotion Engine is disabled. Draft generation and saving are unavailable.
            </div>
          )}
          {sourceLoading && <p role="status">Loading promotion source...</p>}

          {error && (
            <div className="alert alert-danger">
              <strong>AI Generation Error</strong>

              <div className="mt-2">
                {error.message}
              </div>
            </div>
          )}

          {imageError && (
            <div className="alert alert-danger">
              <strong>AI Image Error</strong>

              <div className="mt-2">
                {imageError.message}
              </div>
            </div>
          )}

          {selectedPost && editingPostId && (
            <div className="alert alert-info">
              Editing Promotion Post:{" "}
              <strong>{selectedPost.title || editingPostId}</strong>
            </div>
          )}

          <div className="row g-4">
            <div className="col-12">
              <PromotionSectionCard
                title="Content Workspace"
                subtitle="Create and edit Promotion Posts."
                icon="bi bi-pencil-square"
              >

              <div className="mb-3">
                <label className="form-label" htmlFor="promotion-ai-prompt">
                  AI Prompt / Idea <span className="text-danger">*</span>
                </label>
                <textarea id="promotion-ai-prompt" required
                  aria-invalid={promptRequired && !prompt.trim()} rows={4}
                  className="form-control" value={prompt}
                  readOnly={saving || Boolean((editingPostId && savedPrompt.trim()) || (!editingPostId && ideaHasPrompt))}
                  onChange={(e) => {
                    if (saving || (editingPostId && savedPrompt.trim()) || (!editingPostId && ideaHasPrompt)) return;
                    setPrompt(e.target.value); if (e.target.value.trim()) setPromptRequired(false);
                  }}
                  placeholder="Describe the main idea and facts for all social posts created from it." />
                <button type="button" className="btn btn-success mt-2" onClick={handleSaveIdea}
                  disabled={Boolean(editingPostId) || saving || postSaving || generating || generatingImage || uploadBusy || sizingBusy || sourceLoading || Boolean(sourceError) || eventLoading || !dataReady || !settings.enabled}>
                  {ideaSaving ? "Saving..." : "Save Idea"}
                </button>
                <div className="form-text">Save the Idea now. Platform, Post Type, and Post Body are required only when saving a Promotion Post.</div>
                <div className="form-text">The shared idea for this workspace and its social posts. Saved promotions keep their own fixed Idea, Platform, and Post Type. Use Create Another Promotion for a new variation.</div>
              </div>

              <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="promotion-platform">
                      Platform <span className="text-danger">*</span>
                    </label>

                    <select
                      id="promotion-platform"
                      className="form-select"
                      value={platform}
                      disabled={Boolean(editingPostId) || uploadBusy || sizingBusy}
                      onChange={(e) => { if (!editingPostId) setPlatform(e.target.value); }}
                    >
                      <option value="">Select...</option>
                      {PROMOTION_ENABLED_PLATFORM_OPTIONS.map((item) => (
                        <option
                          key={item.value}
                          value={item.value}
                        >
                          {item.label}
                        </option>
                      ))}
                    </select>

                    <div className="form-text">
                      {platform && (platform !== "facebook" || postCapabilities.baseType !== "feed_text")
                        ? "Options are saved with this draft. Direct publishing for this Platform and Post Type is not yet available."
                        : "Choose the social destination for this draft."}
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="promotion-post-type">
                      Post Type
                    </label>

                    <select
                      id="promotion-post-type"
                      className="form-select"
                      value={postType}
                      disabled={Boolean(editingPostId) || !platform || uploadBusy || sizingBusy}
                      onChange={(e) => { if (!editingPostId) setPostType(e.target.value); }}
                    >
                      <option value="">Select...</option>
                      {postTypeOptions.map((item) => (
                        <option
                          key={item.value}
                          value={item.value}
                        >
                          {item.label}
                        </option>
                      ))}
                    </select>
                    <button type="button" className="btn btn-link p-0 mt-2" onClick={() => setShowPostSizes(true)}>Post Sizes</button>
                  </div>
                </div>


                <div className="row">
                  {!isManager && <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="promotion-event-type">
                      Event Type
                    </label>

                    <select
                      id="promotion-event-type"
                      className="form-select"
                      value={eventType}
                      disabled={Boolean(eventId)}
                      onChange={(e) => setEventType(e.target.value)}
                    >
                      <option value="">All Event Types</option>
                      {EVENT_TYPES.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>

                    <div className="form-text">
                      {eventId
                        ? eventDoc?.eventType ? "Inherited from the current event."
                          : "This event has no Event Type. Drafts use general LGV context until its event record is updated."
                        : "Sets the event market and audience context for the draft."}
                    </div>
                  </div>}
                  {!isManager && <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="promotion-content-category">
                      Content Category
                    </label>

                    <select
                      id="promotion-content-category"
                      className="form-select"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      <option value="">Select...</option>
                      {PROMOTION_CONTENT_CATEGORIES.map((item) => (
                        <option
                          key={item.key}
                          value={item.key}
                        >
                          {item.label}
                        </option>
                      ))}
                    </select>

                    <div className="form-text">
                      Classifies the content for organization, rotation, and reporting.
                    </div>
                  </div>}
                </div>

                <div className="mb-3">
                  <label className="form-label" htmlFor="promotion-post-body">
                    {postCapabilities.supportsCaption === false && postCapabilities.supported ? "Draft Notes" : "Post Body"} <span className="text-danger">*</span>
                  </label>

                  <textarea
                    id="promotion-post-body"
                    required
                    rows={10}
                    className="form-control"
                    value={body}
                    maxLength={postCapabilities.supportsCaption ? postCapabilities.captionLimit : undefined}
                    onChange={(e) => setBody(e.target.value)}
                  />
                  {postCapabilities.supported && !postCapabilities.supportsCaption && <div className="form-text">These notes guide drafting. Caption and hashtag controls are not enabled for this format.</div>}
                </div>

                <div className="mb-3">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    disabled={
                      saving || generating || contentLimitReached ||
                      sourceLoading || Boolean(sourceError) || sizingBusy ||
                      !settings.enabled ||
                      !settings.aiEnabled ||
                      eventLoading ||
                      !dataReady ||
                      !platform.trim()

                    }
                    onClick={handleGenerateAiDraft}
                  >
                    {generating
                      ? "Generating AI..."
                      : "Generate AI Draft"}
                  </button>
                {isManager && <p className="form-text" role="status">{aiUsageError || (aiUsageLoading || !aiUsage ? "Checking remaining content runs..." : `Content creation remaining: ${aiUsage.contentRemaining} of 2 runs for this draft.`)}</p>}
                </div>

                <div className="row">
                  {postCapabilities.supportsHashtags && <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="promotion-hashtags">
                      Hashtags
                    </label>

                    <input
                      id="promotion-hashtags"
                      className="form-control"
                      value={hashtags}
                      onChange={(e) => setHashtags(e.target.value)}
                      placeholder="#LetsGoVote #EventTechnology"
                    />
                    <div className="form-text">
                      LGV draft limit: {postCapabilities.hashtagLimit} hashtags. Added to the caption when published; tags already in Post Body are included once.
                    </div>
                  </div>}

                  {postCapabilities.supportsCaption && <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="promotion-cta">
                      Call to Action Text
                    </label>

                    <input
                      id="promotion-cta"
                      className="form-control"
                      value={cta}
                      onChange={(e) => setCta(e.target.value)}
                    />
                    <div className="form-text">Appended as caption text. This does not create a native social button.</div>
                  </div>}
                </div>

                <PromotionPostOptions capabilities={postCapabilities} value={postOptions} onChange={setPostOptions}
                  disabled={generating || generatingImage || sourceLoading || Boolean(sourceError) || saving || postSaving || uploadBusy || sizingBusy} />

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="promotion-message-focus">
                      Message Focus
                    </label>

                    <input
                      id="promotion-message-focus"
                      className="form-control"
                      list="promotion-message-focus-options"
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      placeholder="Example: QR-Code Voting, Sponsors, Event Reminder"
                    />

                    <datalist id="promotion-message-focus-options">
                      {PROMOTION_MESSAGE_FOCUS_OPTIONS.map((item) => (
                        <option
                          key={item.value}
                          value={item.value}
                        >
                          {item.label}
                        </option>
                      ))}
                    </datalist>

                    <div className="form-text">
                      Optional. Guides the AI topic and reference knowledge; this field is not posted.
                    </div>
                  </div>


                </div>

                {maxImages > 0 && <>
                <div className="mb-3">
                  <label className="form-label" htmlFor="promotion-image-prompt">
                    AI Image Prompt
                  </label>

                  <textarea
                    id="promotion-image-prompt"
                    rows={3}
                    className="form-control"
                    value={imagePrompt}
                    onChange={(e) => setImagePrompt(e.target.value)}
                  />
                                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={
                      generatingImage || imageLimitReached ||
                      sourceLoading || Boolean(sourceError) || sizingBusy ||
                      uploadBusy || !maxImages || images.length >= maxImages ||
                      !settings.enabled ||
                      !settings.imageGenerationEnabled ||
                      eventLoading ||
                      !dataReady ||
                      !body
                    }
                    onClick={handleGenerateImage}
                  >
                    {generatingImage
                      ? "Generating Image..."
                      : "Generate AI Image"}
                  </button>
                  {isManager && <p className="form-text" role="status">{aiUsageError || (aiUsageLoading || !aiUsage ? "Checking remaining image runs..." : `Image creation remaining: ${aiUsage.imageRemaining} of 2 runs for this draft.`)}</p>}
                </div>
                </>}

                {(maxImages > 0 || images.length > 0) && <PromotionImageUpload eventId={eventId || null} images={images} maxImages={maxImages}
                  onChange={setImages} onPreview={(index) => setImagePreview({ images, index })} onBusyChange={setUploadBusy} disabled={sizingBusy || sourceLoading || Boolean(sourceError) || saving || postSaving || generatingImage}
                  onError={(message) => setNotification({ show: true, type: "error", message })} />}

                {videoConstraints && <PromotionVideoUpload key={`${editorIdentity}:${platform}:${postType}`}
                  eventId={eventId || null} platform={platform} postType={postType} video={rawVideo}
                  onChange={setRawVideo} onBusyChange={setVideoUploadBusy}
                  disabled={sizingBusy || sourceLoading || Boolean(sourceError) || saving || postSaving || generatingImage || generating}
                  onError={(message) => setNotification({ show: true, type: "error", message })} />}

                {maxImages > 0 && <section className="border rounded p-3 mb-3" aria-labelledby="promotion-editor-sized-images-title">
                  <h3 className="h6" id="promotion-editor-sized-images-title">Sized Images</h3>
                  <p className="small text-muted">Drag and zoom each original inside a fixed frame. Use full image keeps the entire photo with white padding. Originals stay unchanged.</p>
                  {!selectedFormat?.width && <p className="small text-muted">Choose a Post Type with dimensions to generate sized copies.</p>}
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {images.map((image, index) => <button key={image.url} type="button" className="btn btn-sm btn-outline-secondary"
                      disabled={sizingBusy || uploadBusy || generating || generatingImage || saving || postSaving || sourceLoading || Boolean(sourceError) || !settings.enabled || !selectedFormat?.width}
                      onClick={() => setCropQueue([image])}>Crop / Resize image {index + 1}</button>)}
                  </div>
                  {sizingBusy && <p className="small mt-2" role="status">{sizingProgress || "Sizing images..."}</p>}
                  {!currentSizedImages.length && <p className="small text-muted mt-2">No sized images for this Post Type yet.</p>}
                  {currentSizedImages.length > 0 && currentSizedImages.length < images.length && <p className="small text-muted mt-2">{currentSizedImages.length} of {images.length} originals have matching sized copies. Use Crop / Resize on each remaining image to complete the gallery.</p>}
                  <div className="d-flex flex-wrap gap-3 mt-2">{currentSizedImages.map((image, index) => <figure className="mb-0" key={image.url}>
                    <button type="button" className="btn p-0 border-0" aria-label={`Preview sized image ${index + 1}`} onClick={() => setImagePreview({ images: currentSizedImages, index })}>
                      <img src={image.url} alt={`Sized promotion ${index + 1}`} style={{ maxWidth: 180, maxHeight: 240, objectFit: "contain" }} />
                    </button>
                    <figcaption className="small">{image.width} × {image.height}</figcaption>
                  </figure>)}</div>
                </section>}

                <div className="d-flex flex-wrap gap-2 mt-4">




                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={generating || generatingImage || sizingBusy || cropQueue.length > 0 || uploadBusy || sourceLoading || Boolean(sourceError) || saving || postSaving || !settings.enabled || eventLoading || !dataReady}
                    onClick={handleSaveDraft}
                  >
                    {editingPostId
                      ? "Save Changes"
                      : "Save Promotion Post"}
                  </button>

                  {isDraftPost && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={generating || generatingImage || sizingBusy || cropQueue.length > 0 || uploadBusy || sourceLoading || Boolean(sourceError) || saving || postSaving || !settings.enabled || eventLoading || !dataReady}
                      onClick={handleScheduleDraft}
                    >
                      Schedule
                    </button>
                  )}

                  {isDraftPost && (
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      disabled={generating || generatingImage || sizingBusy || uploadBusy || sourceLoading || Boolean(sourceError) || saving || postSaving}
                      onClick={handleDeleteDraft}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </PromotionSectionCard>

              <PromotionSectionCard title="Promotions from this Idea" subtitle="Saved promotions linked to the current idea.">
                {!!ideaPosts.length && <p className="small text-muted">Each Platform and Request Type record has its own schedule. Save content changes before scheduling.</p>}
                {!selectedIdea && <p className="text-muted">Save a promotion or open an idea to see its promotions.</p>}
                {ideaPostsLoading && <p role="status">Loading promotions...</p>}
                {ideaPostsError && <div className="alert alert-danger" role="alert">{ideaPostsError}
                  <button type="button" className="btn btn-link" onClick={() => setIdeaPostsRevision((revision) => revision + 1)}>Retry</button>
                </div>}
                {selectedIdea && !ideaPostsLoading && !ideaPostsError && !ideaPosts.length && <p className="text-muted">No promotions from this idea yet.</p>}
                {!!ideaPosts.length && <div className="table-responsive"><table className="table align-middle">
                  <thead><tr><th scope="col">Request Type</th><th scope="col">Platform</th><th scope="col">Post Type</th><th scope="col">Status / Date</th><th scope="col">Promotion</th><th scope="col">Scheduling</th></tr></thead>
                  <tbody>{ideaPosts.map((post) => <tr key={post.id}>
                    <td>{({ social_post: "Social Post", prospect_outreach: "Prospect Outreach", website_copy: "Website Copy", seo_copy: "SEO Copy", email_copy: "Email Copy" })[post.requestType] || post.requestType || "Social Post"}</td>
                    <td>{PROMOTION_ENABLED_PLATFORM_OPTIONS.find((option) => option.value === post.platform)?.label || post.platform}</td>
                    <td>{getPromotionPostTypeLabel(post.postType)}</td>
                    <td>{getPromotionHistoryLabel(post)}
                      {getPromotionSocialPostUrl(post) && <a className="d-block" href={getPromotionSocialPostUrl(post)} target="_blank" rel="noopener noreferrer">View social post</a>}
                    </td>
                    <td><button type="button" className="btn btn-link p-0" disabled={uploadBusy || sizingBusy} onClick={() => navigate(`${promotionUrl("promotion_studio")}?post=${encodeURIComponent(post.id)}`)}>{post.title || "Open promotion"}</button>
                      <button type="button" className="btn btn-link d-block p-0" onClick={() => {
                        if (post.sizedImages?.length) setImagePreviewPost(post);
                        else setImagePreview({ images: (Array.isArray(post.imageUrls) ? post.imageUrls : post.imageUrl ? [post.imageUrl] : []).map((url) => ({ url })), index: 0 });
                      }}>{post.sizedImages?.length ? "View Sized Images" : "View Images"}</button></td>
                    <td id={`promotion-schedule-${post.id}`}><PromotionScheduleForm post={post}
                      onSchedule={(values) => handleScheduleRecord(post, values)} onNotification={setNotification}
                      schedulerEnabled={settings.enabled && settings.schedulerEnabled}
                      disabled={generating || generatingImage || sizingBusy || cropQueue.length > 0 || uploadBusy || sourceLoading || Boolean(sourceError) || saving || postSaving || ideaPostsLoading} /></td>
                  </tr>)}</tbody>
                </table></div>}
                {selectedIdea && <button type="button" className="btn btn-outline-primary" disabled={uploadBusy || sizingBusy} onClick={() => navigate(`${promotionUrl("promotion_studio")}?idea=${encodeURIComponent(selectedIdea.id)}`)}>Create Another Promotion</button>}
              </PromotionSectionCard>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PromotionContentStudio;
